import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Droplets, Zap, Shield, ChevronRight, Scale, Check } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import { getProductsByCategory, getBrand, getCategory } from '../../data/products';
import { useCompare } from '../../contexts/CompareContext';

const HeatPumpsCategoryPage = () => {
  const category = getCategory('heat-pumps');
  const items = getProductsByCategory('heat-pumps');
  const { toggle, isSelected, count, max } = useCompare();

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://supaenergyhub.com.au/' },
        { '@type': 'ListItem', position: 2, name: 'Heat Pumps', item: 'https://supaenergyhub.com.au/products/heat-pumps' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: items.map((p, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://supaenergyhub.com.au/products/heat-pumps/${p.slug}`,
        name: p.name,
      })),
    },
  ];

  return (
    <div className="min-h-screen">
      <SEO
        title="Heat Pump Hot Water Systems — All Models"
        description="Browse all heat pump hot water products available through Supa Energy Hub. Compare tank sizes, COP ratings and rebate-eligible models from leading Australian brands."
        jsonLd={jsonLd}
      />
      <Header />
      <main>
        {/* Breadcrumb + Hero */}
        <section className="pt-32 pb-12 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" data-testid="category-breadcrumb">
              <Link to="/" className="hover:text-[#3D5A47]">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-[#3D5A47] font-medium">Heat Pumps</span>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-3">
                Category · {category.tagline}
              </span>
              <h1 className="heading-1 mb-4 text-[#3D5A47]">
                Heat Pump Hot Water <span className="text-[#B5D43B]">Systems</span>
              </h1>
              <p className="body-large text-gray-600">{category.description}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10 max-w-3xl">
              {[
                { icon: Zap, label: 'Up to 75% less energy', sub: 'vs. electric storage' },
                { icon: Droplets, label: 'STC + state rebates', sub: 'applied upfront' },
                { icon: Shield, label: 'CEC-accredited install', sub: 'by Supa Energy Hub' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <div className="w-10 h-10 rounded-lg bg-[#B5D43B]/15 flex items-center justify-center mb-2">
                    <s.icon className="w-5 h-5 text-[#3D5A47]" />
                  </div>
                  <p className="font-semibold text-sm text-[#3D5A47]">{s.label}</p>
                  <p className="text-xs text-gray-500">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <h2 className="heading-2 text-[#3D5A47]">Available Models</h2>
                <p className="text-gray-600 mt-1">Featured products from our trusted manufacturers.</p>
              </div>
              <Link to="/heat-pump" className="text-sm font-medium text-[#3D5A47] hover:text-[#B5D43B] inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to Heat Pump Overview
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map((p) => {
                const brand = getBrand(p.brand);
                const selected = isSelected(p.slug);
                const limitReached = !selected && count >= max;
                return (
                  <div
                    key={p.slug}
                    className={`group relative bg-white rounded-2xl border ${selected ? 'border-[#B5D43B] ring-2 ring-[#B5D43B]/30' : 'border-gray-200 hover:border-[#B5D43B]/60'} hover:shadow-xl transition-all overflow-hidden flex`}
                    data-testid={`product-card-${p.slug}`}
                  >
                    {/* Compare checkbox */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        if (!limitReached) toggle(p.slug);
                      }}
                      disabled={limitReached}
                      className={`absolute top-3 right-3 z-20 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1.5 rounded-lg shadow-sm transition-all ${
                        selected
                          ? 'bg-[#B5D43B] text-[#1a2820]'
                          : limitReached
                          ? 'bg-white/70 text-gray-300 cursor-not-allowed'
                          : 'bg-white text-[#3D5A47] hover:bg-[#B5D43B] hover:text-[#1a2820]'
                      }`}
                      aria-pressed={selected}
                      aria-label={selected ? `Remove ${p.name} from comparison` : `Add ${p.name} to comparison`}
                      data-testid={`compare-toggle-${p.slug}`}
                    >
                      {selected ? <Check className="w-3 h-3 pointer-events-none" /> : <Scale className="w-3 h-3 pointer-events-none" />}
                      {selected ? 'Compare' : limitReached ? 'Max' : 'Compare'}
                    </button>

                    <Link
                      to={`/products/heat-pumps/${p.slug}`}
                      className="flex flex-1 min-w-0"
                      data-testid={`product-card-link-${p.slug}`}
                    >
                      {/* Left: product image with tank-size badge */}
                      <div className="w-40 sm:w-48 flex-shrink-0 bg-gradient-to-br from-[#3D5A47] to-[#1a2820] relative flex items-center justify-center p-3">
                        <div className="absolute top-3 left-3 z-10 bg-[#B5D43B] text-[#1a2820] rounded-lg px-2 py-1.5 text-center shadow-md">
                          <span className="block text-[7px] uppercase tracking-widest font-bold leading-none">Tank</span>
                          <div className="flex items-baseline justify-center leading-none mt-0.5">
                            <span className="text-xl font-black">{p.headlineSpec.value}</span>
                            <span className="text-xs font-black">{p.headlineSpec.unit}</span>
                          </div>
                        </div>
                        {p.image && (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="h-36 w-auto object-contain"
                            style={{ mixBlendMode: 'multiply' }}
                            loading="lazy"
                          />
                        )}
                        <span className="absolute bottom-2 left-0 right-0 text-center text-[8px] text-white/40 uppercase tracking-widest font-mono">
                          {p.series}
                        </span>
                      </div>

                      {/* Right: Info */}
                      <div className="p-5 pr-16 flex flex-col flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <img src={brand.logo} alt={brand.name} className="h-5 w-auto" />
                          <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
                            {brand.name}
                          </span>
                        </div>
                        <h3 className="font-bold text-lg text-[#3D5A47] group-hover:text-[#B5D43B] transition-colors leading-tight">
                          {p.name.split(' (')[0]}
                        </h3>
                        <p className="text-xs text-gray-400 mb-2 font-mono">Model {p.model}</p>
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 flex-1">
                          {p.tagline}
                        </p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3D5A47] bg-[#B5D43B]/15 px-2 py-1 rounded-full">
                            {p.heroPills?.[0]?.label || 'View'}
                          </span>
                          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#3D5A47] group-hover:text-[#B5D43B]">
                            View Specs <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Need help choosing?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Our team will recommend the right tank size and model based on your household, climate zone and rebates available.
            </p>
            <Link to="/get-estimate">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2" data-testid="category-cta-estimate">
                Get a Free Estimate <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HeatPumpsCategoryPage;
